import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { BossSkillEffectRelation, BossEffect } from "../../types";
import { skillEffectRelationApi, effectApi } from "../../services/api";
import { supabase } from "../../lib/supabase";
import { changedFields } from "../../utils/changedFields";
import { seconds } from "../../utils/resourcePresentation";
import UnifiedModal from "./UnifiedModal";

const { Text } = Typography;
interface Props {
  visible: boolean;
  onCancel: () => void;
  skillKey: string;
  skillName: string;
  bossCode: string;
}
const defaults = {
  power: 1,
  order: 1,
  priority: 1,
  duration_s: 0,
  add_count: 0,
  property_category: "PHYSICAL",
  property_element: "NONE",
};
const relationKey = (row: BossSkillEffectRelation) =>
  JSON.stringify([row.skill, row.effect, row.order]);

const SkillEffectRelationsModal: React.FC<Props> = ({
  visible,
  onCancel,
  skillKey,
  skillName,
  bossCode,
}) => {
  const [form] = Form.useForm();
  const [notice, noticeHolder] = message.useMessage();
  const [relations, setRelations] = useState<BossSkillEffectRelation[]>([]);
  const [effects, setEffects] = useState<BossEffect[]>([]);
  const [categories, setCategories] = useState<any[]>([]),
    [elements, setElements] = useState<any[]>([]);
  const [editing, setEditing] = useState<BossSkillEffectRelation | null>(null);
  const [loading, setLoading] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const [newEffect, setNewEffect] = useState(false);
  const request = useRef(0),
    mutation = useRef(false);
  const reset = useCallback(() => {
    setEditing(null);
    form.resetFields();
  }, [form]);
  const load = useCallback(async () => {
    if (!visible || !skillKey) return;
    const id = ++request.current;
    setLoading(true);
    setError("");
    try {
      const [rows, allEffects, cats, elems] = await Promise.all([
        skillEffectRelationApi.getBySkill(skillKey),
        effectApi.getAll(),
        supabase
          .from("inf_property_category")
          .select("*")
          .order("display_order"),
        supabase
          .from("inf_property_element")
          .select("*")
          .order("display_order"),
      ]);
      if (cats.error) throw cats.error;
      if (elems.error) throw elems.error;
      if (id !== request.current) return;
      const linked = new Set(rows.map((row) => row.effect));
      setRelations(rows);
      setEffects(
        allEffects.filter(
          (effect) =>
            effect.boss_code === (bossCode || "COMMON") ||
            effect.boss_code === "COMMON" ||
            effect.boss_code == null ||
            linked.has(effect.key),
        ),
      );
      setCategories(cats.data || []);
      setElements(elems.data || []);
    } catch (e: any) {
      if (id === request.current)
        setError(e.message || "연결 데이터를 불러오지 못했습니다.");
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [visible, skillKey, bossCode]);
  useEffect(() => {
    reset();
    setRelations([]);
    setEffects([]);
    setNewEffect(false);
    load();
    const refresh = () => {
      load();
    };
    window.addEventListener("skillsUpdated", refresh);
    return () => {
      request.current++;
      window.removeEventListener("skillsUpdated", refresh);
    };
  }, [load, reset]);
  const finish = () => {
    reset();
    window.dispatchEvent(new Event("skillsUpdated"));
  };
  const save = async (values: any) => {
    if (mutation.current || loading || error) return;
    mutation.current = true;
    setSaving(true);
    try {
      if (editing) {
        const patch = changedFields(editing, values);
        if (Object.keys(patch).length)
          await skillEffectRelationApi.update(editing, patch);
      } else
        await skillEffectRelationApi.create({ ...values, skill: skillKey });
      notice.success(
        editing ? "효과 연결을 수정했습니다." : "효과 연결을 추가했습니다.",
      );
      finish();
    } catch (e: any) {
      notice.error(
        "저장에 실패했습니다. 입력 내용은 유지됩니다. " + (e.message || ""),
      );
    } finally {
      mutation.current = false;
      setSaving(false);
    }
  };
  const remove = async (row: BossSkillEffectRelation) => {
    if (mutation.current) return;
    mutation.current = true;
    setSaving(true);
    try {
      await skillEffectRelationApi.delete(row);
      notice.success("효과 연결을 삭제했습니다.");
      if (editing && relationKey(editing) === relationKey(row)) reset();
      window.dispatchEvent(new Event("skillsUpdated"));
    } catch (e: any) {
      notice.error("삭제에 실패했습니다. " + (e.message || ""));
    } finally {
      mutation.current = false;
      setSaving(false);
    }
  };
  const edit = (row: BossSkillEffectRelation) => {
    reset();
    setEditing(row);
    // Only populate editable columns; do not leak hidden fields into updates.
    form.setFieldsValue(
      Object.fromEntries(
        ["effect", ...Object.keys(defaults)].map((key) => [
          key,
          (row as any)[key] ?? null,
        ]),
      ),
    );
  };
  const locked = loading || saving || !!error;
  const effectName = (key: string) =>
    effects.find((effect) => effect.key === key)?.name || key;
  return (
    <>
      {noticeHolder}
      <Modal
        title={"스킬-효과 연결 관리: " + skillName}
        open={visible}
        onCancel={() => {
          if (!saving) onCancel();
        }}
        width={1000}
        maskClosable={!saving}
        closable={!saving}
        footer={
          <Button disabled={saving} onClick={onCancel}>
            닫기
          </Button>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="스킬에 적용할 효과와 위력·지속시간·적용 순서를 설정합니다."
            description="효과의 적용 대상은 스킬과 효과 탭에서 관리합니다. -1초는 무제한, 0초는 즉시 적용입니다."
          />
          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              action={<Button onClick={load}>다시 불러오기</Button>}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={defaults}
            onFinish={save}
            disabled={locked}
          >
            <Text strong>{editing ? "선택한 연결 수정" : "새 효과 연결"}</Text>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col xs={24} md={18}>
                <Form.Item
                  name="effect"
                  label="적용할 효과"
                  rules={[{ required: true, message: "효과를 선택하세요." }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="효과 이름 또는 코드 검색"
                    loading={loading}
                    options={effects.map((effect) => ({
                      value: effect.key,
                      label: effect.name + " (" + effect.key + ")",
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Button
                  style={{ marginTop: 30 }}
                  disabled={locked}
                  onClick={() => setNewEffect(true)}
                >
                  새 효과 만들기
                </Button>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <Form.Item name="power" label="위력 값">
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  name="duration_s"
                  label="지속시간(초)"
                  tooltip="-1: 무제한 / 0: 즉시 적용"
                >
                  <InputNumber min={-1} step={0.1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="add_count" label="중첩 증가">
                  <InputNumber precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  name="order"
                  label="적용 순서"
                  rules={[
                    { required: true, message: "적용 순서를 입력하세요." },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="property_category" label="속성 카테고리">
                  <Select
                    allowClear
                    options={categories.map((c) => ({
                      value: c.category_code,
                      label: c.category_name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="property_element" label="속성 원소">
                  <Select
                    allowClear
                    options={elements.map((e) => ({
                      value: e.element_code,
                      label: e.element_name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="priority" label="우선순위">
                  <InputNumber precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                disabled={locked}
              >
                {editing ? "수정 저장" : "연결 추가"}
              </Button>
              {editing && (
                <Button onClick={reset} disabled={saving}>
                  편집 취소
                </Button>
              )}
            </Space>
          </Form>
          <Table<BossSkillEffectRelation>
            rowKey={relationKey}
            dataSource={relations}
            size="small"
            pagination={false}
            loading={loading}
            tableLayout="fixed"
            scroll={{ x: 940 }}
            columns={[
              {
                title: "순서",
                dataIndex: "order",
                width: 60,
                sorter: (a, b) => a.order - b.order,
              },
              {
                title: "효과",
                dataIndex: "effect",
                width: 240,
                render: (key) => (
                  <div style={{ overflowWrap: "anywhere" }}>
                    <Text strong>{effectName(key)}</Text>
                    <br />
                    <Text type="secondary">{key}</Text>
                  </div>
                ),
              },
              { title: "위력", dataIndex: "power", width: 80 },
              { title: "계산", width: 70, render: (_, row) => effects.find(effect => effect.key === row.effect)?.operator || "미설정" },
              { title: "우선순위", dataIndex: "priority", width: 80 },
              {
                title: "지속시간",
                dataIndex: "duration_s",
                width: 90,
                render: (value) => seconds(value),
              },
              { title: "중첩 증가", dataIndex: "add_count", width: 80 },
              {
                title: "속성",
                width: 130,
                render: (_, row) => (
                  <Space direction="vertical" size={0}>
                    <Text>
                      {categories.find(
                        (c) => c.category_code === row.property_category,
                      )?.category_name ||
                        row.property_category ||
                        "미설정"}
                    </Text>
                    <Text type="secondary">
                      {elements.find(
                        (e) => e.element_code === row.property_element,
                      )?.element_name ||
                        row.property_element ||
                        "미설정"}
                    </Text>
                  </Space>
                ),
              },
              {
                title: "작업",
                width: 130,
                render: (_, row) => (
                  <Space>
                    <Button
                      size="small"
                      disabled={locked}
                      onClick={() => edit(row)}
                    >
                      편집
                    </Button>
                    <Popconfirm
                      title="이 효과 연결을 삭제할까요?"
                      description={effectName(row.effect)}
                      onConfirm={() => remove(row)}
                      okText="삭제"
                      cancelText="취소"
                      disabled={locked}
                    >
                      <Button size="small" danger disabled={locked}>
                        삭제
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      </Modal>
      {newEffect && (
        <UnifiedModal
          visible
          onCancel={() => setNewEffect(false)}
          modalType="effect"
          mode="create"
          currentBossCode={bossCode || "COMMON"}
          onOk={async (values) => {
            try {
              await effectApi.create({
                ...values,
                boss_code: bossCode || "COMMON",
                game_version: "v1.0",
              } as any);
              setNewEffect(false);
              window.dispatchEvent(new Event("skillsUpdated"));
              notice.success(
                "효과를 만들었습니다. 적용할 효과에서 선택하세요.",
              );
            } catch (e) {
              notice.error("효과 저장에 실패했습니다. 입력 내용은 유지됩니다.");
              throw e;
            }
          }}
        />
      )}
    </>
  );
};
export default SkillEffectRelationsModal;
